/**
 * AuthorDetailView — Stage view showing a single philosopher's scheda.
 *
 * Loads the author via IDataService.getAuthor on init. Biographical relations
 * (maestri, allievi, influenzato_da, ha_influenzato) are rendered as clickable
 * chips that emit navigate:author for in-catalog ids. External relations
 * (outside the 52-author target list) are ignored in D1.
 *
 * Sections "Storia dei concetti" and "Tappe" show honest empty states until the
 * editorial content (Fase C Stage 6 + later) is written.
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { ComponentContext } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { PhilosopherLocalized, TappaLocalized, ConceptLocalized, ArgomentoLocalized } from '@/data/schema';
import { formatLifespan, problemaLabel } from './format';
import { mountAuthorMap } from '@/components/AuthorMapView';

export class AuthorDetailView extends BaseComponent {
  private readonly authorId: string;
  private author: PhilosopherLocalized | null = null;
  private idToName: Map<string, string> = new Map();
  private tappe: TappaLocalized[] = [];
  private conceptMap: Map<string, ConceptLocalized> = new Map();
  private argomenti: ArgomentoLocalized[] = [];
  /** Il click-listener è delegato su this.element (persiste al re-render): va
   *  attaccato UNA sola volta, altrimenti si accumula a ogni display:change e i
   *  clic (es. "← Indietro") sparano più volte. */
  private wired = false;

  constructor(element: HTMLElement, context: ComponentContext, authorId: string) {
    super(element, context);
    this.authorId = authorId;
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('author-detail-view');
    const data = getDataService();
    this.author = await data.getAuthor(this.authorId, this.context.locale);

    if (!this.author) {
      this.renderNotFound();
      return;
    }

    // Build id → nome map for relation chip labels.
    const [all, tappe, concepts, argomenti] = await Promise.all([
      data.listAuthors({}, this.context.locale),
      data.getTappeByAuthor(this.authorId, this.context.locale),
      data.listConcepts(this.context.locale),
      data.getArgomentiByAuthor(this.authorId, this.context.locale),
    ]);
    this.idToName = new Map(all.map((a) => [a.id, a.nome]));
    this.tappe = tappe;
    this.conceptMap = new Map(concepts.map((c) => [c.id, c]));
    this.argomenti = argomenti;

    if (this.isLimMode()) {
      this.renderLimMap();
    } else {
      this.render();
      this.wireEvents();
    }

    // React to display mode changes
    this.addCleanup(
      this.context.bus.on('display:change', () => {
        if (!this.author) return;
        this.element.innerHTML = '';
        if (this.isLimMode()) {
          this.renderLimMap();
        } else {
          this.render();
          this.wireEvents();
        }
      }),
    );
  }

  private isLimMode(): boolean {
    return document.documentElement.getAttribute('data-display') === 'lim';
  }

  private renderLimMap(): void {
    // Argomenti e concetti aprono il loro DRILL-IN (riassunti a mo di mappa)
    // DENTRO la mappa autore — nessuna navigazione via (vedi AuthorMapView).
    mountAuthorMap(this.element, this.authorId, this.context.locale as 'it' | 'en').then(() => {
      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'map-back-btn lim-back-home';
      backBtn.textContent = '← Indietro';
      backBtn.addEventListener('click', () => {
        this.context.bus.emit('navigate:back', {});
      });
      this.element.appendChild(backBtn);
    });
  }

  private renderNotFound(): void {
    this.element.innerHTML = `
      <div class="empty-state">
        <p>Filosofo non trovato: <code>${this.authorId}</code></p>
        <button type="button" class="btn" data-action="home">Torna alla lista</button>
      </div>
    `;
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.dataset.action === 'home') {
        this.context.bus.emit('navigate:home', {});
      }
    });
  }

  private render(): void {
    const a = this.author!;

    const scuole = (a.scuola ?? []).map((s) => `<span class="chip chip-muted">${escapeText(s)}</span>`).join('');
    const problemi = (a.problemi_fondamentali_effettivi ?? [])
      .map((p) => `<span class="chip chip-problema">${escapeText(problemaLabel(p))}</span>`)
      .join('');

    const opere = (a.opere ?? [])
      .filter((o) => o.opera_principale)
      .map(
        (o) =>
          `<li class="opera-item"><em>${escapeText(o.titolo_originale)}</em>${
            o.anno_composizione ? ` <span class="opera-year">(${o.anno_composizione})</span>` : ''
          }</li>`,
      )
      .join('');

    // NIENTE ritratto di sfondo: come il golden, la scheda resta PULITA (il
    // ritratto vive solo nel catalogo/home). Le foto non sono studiate per il
    // dettaglio e si "storpiano" rubando spazio/leggibilità ai tasti.
    this.element.innerHTML = `
      <button type="button" class="btn btn-back" data-action="back">← Indietro</button>

      <header class="detail-header">
        <h1 class="detail-name">${escapeText(a.nome)}</h1>
        ${a.nome_originale && a.nome_originale !== a.nome ? `<p class="detail-native">${escapeText(a.nome_originale)}</p>` : ''}
        <p class="detail-lifespan">${escapeText(formatLifespan(a.anno_nascita, a.anno_morte))}</p>
        ${a.luogo_nascita ? `<p class="detail-place">${escapeText(a.luogo_nascita)}${a.luogo_morte && a.luogo_morte !== a.luogo_nascita ? ` → ${escapeText(a.luogo_morte)}` : ''}</p>` : ''}
        <div class="chips">
          ${scuole}${problemi}
        </div>
      </header>

      ${this.renderArgomentiSection()}
      ${this.renderTappeSection()}
      ${this.renderMetaFooter(opere)}
    `;
  }

  /** Meta compatta in FONDO (come inglese): info statiche brevi — opere,
   *  relazioni, concetti — in linea, con etichetta piccola, non invadenti. */
  private renderMetaFooter(opereListHtml: string): string {
    const a = this.author!;
    const blocks: string[] = [];
    if (opereListHtml) {
      blocks.push(this.metaBlock('Opere', `<ul class="opera-list" data-fragment-id="philosopher:${escapeAttr(a.id)}/opere">${opereListHtml}</ul>`));
    }
    blocks.push(this.metaRelBlock('Influenzato da', a.influenzato_da));
    blocks.push(this.metaRelBlock('Maestri', a.maestri));
    blocks.push(this.metaRelBlock('Ha influenzato', a.ha_influenzato));
    blocks.push(this.metaRelBlock('Allievi', a.allievi));
    blocks.push(this.metaConceptsBlock());
    const inner = blocks.filter(Boolean).join('');
    return inner ? `<footer class="detail-meta">${inner}</footer>` : '';
  }

  private metaBlock(label: string, bodyHtml: string): string {
    return `<div class="detail-meta-block"><span class="detail-meta-label">${escapeText(label)}</span><div class="detail-meta-body">${bodyHtml}</div></div>`;
  }

  private metaRelBlock(label: string, ids: string[]): string {
    const inCatalog = ids.filter((id) => this.idToName.has(id));
    if (inCatalog.length === 0) return '';
    const chips = inCatalog
      .map((id) => `<button type="button" class="chip chip-link" data-author-link="${escapeAttr(id)}">${escapeText(this.idToName.get(id) ?? id)}</button>`)
      .join('');
    return this.metaBlock(label, `<div class="chips chips-inline">${chips}</div>`);
  }

  private metaConceptsBlock(): string {
    const conceptIds = [...new Set(this.tappe.map((t) => t.concetto_id))];
    if (conceptIds.length === 0) return '';
    const chips = conceptIds
      .map((id) => {
        const c = this.conceptMap.get(id);
        return `<button type="button" class="chip chip-link" data-concept-link="${escapeAttr(id)}">${escapeText(c?.nome ?? id)}</button>`;
      })
      .join('');
    return this.metaBlock('Concetti', `<div class="chips chips-inline">${chips}</div>`);
  }

  private renderArgomentiSection(): string {
    if (this.argomenti.length === 0) return '';
    const id = this.author?.id ?? '';
    const items = this.argomenti
      .map((a) => {
        const enunciato = a.content.enunciato.length > 220
          ? a.content.enunciato.slice(0, 220).replace(/\s+\S*$/, '…')
          : a.content.enunciato;
        const anno = a.anno_formulazione ? ` <span class="argomento-card-anno">(${a.anno_formulazione})</span>` : '';
        return `
          <button type="button" class="argomento-card" data-argomento-link="${escapeAttr(a.id)}">
            <span class="argomento-card-name">${escapeText(a.nome)}${anno}</span>
            <span class="argomento-card-statement">${escapeText(enunciato)}</span>
          </button>
        `;
      })
      .join('');
    return `<section class="detail-section detail-tappe-fullwidth">
      <h2 class="detail-content-title" data-fragment-id="philosopher:${escapeAttr(id)}/argomenti/titolo">Argomenti <span class="info-hint" title="I grandi ragionamenti filosofici dell'autore, sviscerati passo per passo">&#9432;</span></h2>
      <div class="argomento-list">${items}</div>
    </section>`;
  }

  private renderTappeSection(): string {
    if (this.tappe.length === 0) return '';
    const id = this.author?.id ?? '';
    const items = this.tappe
      .map((t) => {
        const concept = this.conceptMap.get(t.concetto_id);
        const conceptName = concept?.nome ?? t.concetto_id;
        const anno = t.anno_riferimento < 0
          ? `${Math.abs(t.anno_riferimento)} a.C.`
          : `${t.anno_riferimento} d.C.`;
        const preview = t.content.spiegazione.slice(0, 280).replace(/\s+\S*$/, '…');
        return `
          <button type="button" class="tappa-card" data-tappa-link="${escapeAttr(t.id)}">
            <span class="tappa-card-concept">${escapeText(conceptName)}</span>
            <span class="tappa-card-anno">${escapeText(anno)}</span>
            <span class="tappa-card-preview">${escapeText(preview)}</span>
          </button>
        `;
      })
      .join('');
    return `<section class="detail-section detail-tappe-fullwidth">
      <h2 class="detail-content-title" data-fragment-id="philosopher:${escapeAttr(id)}/tappe/titolo">Tappe <span class="info-hint" title="Le posizioni dell'autore sui grandi temi della filosofia">&#9432;</span></h2>
      <div class="tappa-list">${items}</div>
    </section>`;
  }

  private wireEvents(): void {
    if (this.wired) return;
    this.wired = true;
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.dataset.action === 'back') {
        this.context.bus.emit('navigate:back', {});
        return;
      }
      if (t.dataset.action === 'home') {
        this.context.bus.emit('navigate:home', {});
        return;
      }
      const link = t.closest<HTMLElement>('[data-author-link]');
      if (link?.dataset.authorLink) {
        this.context.bus.emit('navigate:author', { id: link.dataset.authorLink });
        return;
      }
      const tappaLink = t.closest<HTMLElement>('[data-tappa-link]');
      if (tappaLink?.dataset.tappaLink) {
        this.context.bus.emit('navigate:tappa', { id: tappaLink.dataset.tappaLink });
        return;
      }
      const conceptLink = t.closest<HTMLElement>('[data-concept-link]');
      if (conceptLink?.dataset.conceptLink) {
        this.context.bus.emit('navigate:concept', { id: conceptLink.dataset.conceptLink, sourceAuthorId: this.authorId });
        return;
      }
      const argomentoLink = t.closest<HTMLElement>('[data-argomento-link]');
      if (argomentoLink?.dataset.argomentoLink) {
        this.context.bus.emit('navigate:argomento', { id: argomentoLink.dataset.argomentoLink });
      }
    });
  }
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, '&quot;');
}
